import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../config";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  LineController,
} from "chart.js";
import { RotateCw, AlertCircle } from "lucide-react";
import "./SalesAnalysisDashboard.css";

// Register ChartJS modules
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  BarController,
  LineController
);

const fmtINR = (v) => "₹" + Math.round(v || 0).toLocaleString("en-IN");
const fmtL = (v) => "₹" + ((v || 0) / 100000).toFixed(1) + "L";
const pct = (v) => (100 * (v || 0)).toFixed(1) + "%";

const SalesAnalysisDashboard = () => {
  const [range, setRange] = useState("mtd");
  const [team, setTeam] = useState("all");
  const [source, setSource] = useState("all");
  const [teamsList, setTeamsList] = useState([]);
  const [sourcesList, setSourcesList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState("collections");
  const [sortDir, setSortDir] = useState(-1);

  // Canvas refs
  const chTrendRef = useRef(null);
  const chFunnelRef = useRef(null);
  const chSrcRef = useRef(null);
  const chTicketRef = useRef(null);
  const chFollowRef = useRef(null);
  const chSlaRef = useRef(null);

  // Chart instances ref to prevent memory leaks
  const chartsRef = useRef({});

  // Fetch filter dropdown options
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const token = localStorage.getItem("authToken");
        const res = await axios.get(`${API_URL}sales-analytics/filters`, {
          headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
        });
        if (res.data?.success && res.data?.data) {
          setTeamsList(res.data.data.teams || []);
          setSourcesList(res.data.data.sources || []);
        }
      } catch (err) {
        console.warn("Could not fetch filter options from backend:", err);
      }
    };
    fetchFilters();
  }, []);

  // Fetch Dashboard Metrics directly from backend API
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("authToken");
      const url = `${API_URL}sales-analytics?range=${range}&team=${team}&source=${source}`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}`, Accept: "*/*" },
      });

      if (res.data?.success && res.data?.data) {
        setData(res.data.data);
      } else {
        setError(res.data?.message || "No data returned from backend");
        setData(null);
      }
    } catch (err) {
      console.error("API Error fetching sales analytics:", err);
      const errMsg = err.response?.data?.message || err.message || "Failed to fetch sales analytics from server";
      setError(errMsg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [range, team, source]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Render Charts whenever real backend data changes
  useEffect(() => {
    if (!data) return;

    // Helper to destroy existing chart instance
    const destroyChart = (id) => {
      if (chartsRef.current[id]) {
        chartsRef.current[id].destroy();
        delete chartsRef.current[id];
      }
    };

    ChartJS.defaults.font.family = "Roboto, system-ui, sans-serif";
    ChartJS.defaults.color = "#4c5566";
    ChartJS.defaults.borderColor = "#e6e9ef";

    // 1. Collections vs Target Daily Chart
    if (chTrendRef.current && data.daily_trend) {
      destroyChart("trend");
      const t = data.daily_trend;
      const cum = [];
      let acc = 0;
      (t.collections || []).forEach((v) => {
        acc += Number(v);
        cum.push(acc);
      });
      const paceEnd = data.kpis?.target_month || 7000000;
      const pace = (t.labels || []).map((_, i) => (paceEnd * (i + 1)) / (t.labels.length || 1));

      chartsRef.current.trend = new ChartJS(chTrendRef.current, {
        data: {
          labels: t.labels || [],
          datasets: [
            {
              type: "bar",
              label: "Daily collections",
              data: t.collections || [],
              backgroundColor: "#2a78d6",
              borderRadius: 4,
              maxBarThickness: 26,
              yAxisID: "y",
            },
            {
              type: "line",
              label: "Cumulative",
              data: cum,
              borderColor: "#1baf7a",
              pointRadius: 0,
              borderWidth: 2,
              tension: 0.2,
              yAxisID: "y1",
            },
            {
              type: "line",
              label: "Target pace",
              data: pace,
              borderColor: "#d03b3b",
              borderDash: [5, 4],
              pointRadius: 0,
              borderWidth: 1.5,
              yAxisID: "y1",
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: {
              labels: { boxWidth: 10, usePointStyle: true, font: { size: 11 } },
            },
            tooltip: {
              callbacks: {
                label: (c) => ` ${c.dataset.label}: ${fmtL(c.parsed.y)}`,
              },
            },
          },
          scales: {
            y: {
              ticks: { callback: (v) => v / 100000 + "L" },
              grid: { drawTicks: false },
            },
            y1: {
              position: "right",
              grid: { display: false },
              ticks: { callback: (v) => v / 100000 + "L" },
            },
            x: {
              grid: { display: false },
              ticks: { maxTicksLimit: 12 },
            },
          },
        },
      });
    }

    // 2. Lead Funnel Chart
    if (chFunnelRef.current && data.funnel) {
      destroyChart("funnel");
      const f = data.funnel;
      chartsRef.current.funnel = new ChartJS(chFunnelRef.current, {
        type: "bar",
        data: {
          labels: f.map((s) => s.stage),
          datasets: [
            {
              data: f.map((s) => s.count),
              backgroundColor: ["#86b6ef", "#5598e7", "#2a78d6", "#184f95"],
              borderRadius: 4,
              maxBarThickness: 30,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (c) => {
                  const i = c.dataIndex;
                  const cur = f[i].count;
                  const prev = i ? f[i - 1].count : cur;
                  return ` ${cur.toLocaleString("en-IN")} — ${
                    i && prev > 0
                      ? (100 * cur / prev).toFixed(1) + "% of previous stage"
                      : "100%"
                  }`;
                },
              },
            },
          },
          scales: {
            x: { grid: { drawTicks: false } },
            y: { grid: { display: false } },
          },
        },
      });
    }

    // 3. Source Performance Chart
    if (chSrcRef.current && data.sources) {
      destroyChart("src");
      const s = data.sources;
      chartsRef.current.src = new ChartJS(chSrcRef.current, {
        type: "bar",
        data: {
          labels: s.map((x) => x.source),
          datasets: [
            {
              label: "₹ per lead",
              data: s.map((x) => x.rev_per_lead),
              backgroundColor: "#2a78d6",
              borderRadius: 4,
              maxBarThickness: 30,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (c) => {
                  const x = s[c.dataIndex];
                  return [
                    ` ₹${x.rev_per_lead.toLocaleString("en-IN")} per lead`,
                    ` ${x.leads.toLocaleString("en-IN")} leads · ${pct(
                      x.conversion
                    )} conv · ${fmtL(x.revenue)}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              grid: { drawTicks: false },
              ticks: { callback: (v) => "₹" + v.toLocaleString("en-IN") },
            },
            y: { grid: { display: false } },
          },
        },
      });
    }

    // 4. Ticket-size Distribution Chart
    if (chTicketRef.current && data.ticket_bands) {
      destroyChart("ticket");
      const t = data.ticket_bands;
      chartsRef.current.ticket = new ChartJS(chTicketRef.current, {
        type: "bar",
        data: {
          labels: t.map((x) => x.band),
          datasets: [
            {
              label: "Orders",
              data: t.map((x) => x.orders),
              backgroundColor: t.map((x) => (x.premium ? "#1baf7a" : "#2a78d6")),
              borderRadius: 4,
              maxBarThickness: 44,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: { legend: { display: false } },
          scales: {
            y: { grid: { drawTicks: false } },
            x: { grid: { display: false } },
          },
        },
      });
    }

    // 5. Follow-up Discipline Chart
    if (chFollowRef.current && data.followups_by_rep) {
      destroyChart("follow");
      const f = (data.followups_by_rep || []).slice(0, 8);
      chartsRef.current.follow = new ChartJS(chFollowRef.current, {
        type: "bar",
        data: {
          labels: f.map((x) => (x.name ? x.name.split(" ")[0] : "Rep")),
          datasets: [
            {
              label: "Due today",
              data: f.map((x) => x.due_today),
              backgroundColor: "#2a78d6",
              borderRadius: 3,
              maxBarThickness: 18,
            },
            {
              label: "Overdue",
              data: f.map((x) => x.overdue),
              backgroundColor: "#eb6834",
              borderRadius: 3,
              maxBarThickness: 18,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { labels: { boxWidth: 10, font: { size: 11 } } },
          },
          scales: {
            x: { stacked: true, grid: { drawTicks: false } },
            y: { stacked: true, grid: { display: false } },
          },
        },
      });
    }

    // 6. SLA by Rep Chart
    if (chSlaRef.current && data.sla_by_rep) {
      destroyChart("sla");
      const s = (data.sla_by_rep || []).slice(0, 8);
      chartsRef.current.sla = new ChartJS(chSlaRef.current, {
        type: "bar",
        data: {
          labels: s.map((x) => (x.name ? x.name.split(" ")[0] : "Rep")),
          datasets: [
            {
              label: "Median first-call (min)",
              data: s.map((x) => x.median_minutes),
              backgroundColor: s.map((x) =>
                x.median_minutes <= 5 ? "#1baf7a" : x.median_minutes <= 15 ? "#eda100" : "#eb6834"
              ),
              borderRadius: 3,
              maxBarThickness: 18,
            },
          ],
        },
        options: {
          indexAxis: "y",
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (c) => ` ${c.parsed.x} min median first call`,
              },
            },
          },
          scales: {
            x: {
              grid: { drawTicks: false },
              title: { display: true, text: "minutes to first call", font: { size: 10 } },
            },
            y: { grid: { display: false } },
          },
        },
      });
    }

    return () => {
      Object.keys(chartsRef.current).forEach((id) => {
        if (chartsRef.current[id]) {
          chartsRef.current[id].destroy();
        }
      });
      chartsRef.current = {};
    };
  }, [data]);

  // Handle Leaderboard Table Sorting
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => prev * -1);
    } else {
      setSortKey(key);
      setSortDir(-1);
    }
  };

  const sortedReps = React.useMemo(() => {
    if (!data?.reps) return [];
    return [...data.reps].sort((a, b) => {
      const va = a[sortKey];
      const vb = b[sortKey];
      if (va === vb) return 0;
      return sortDir * (va > vb ? 1 : -1);
    });
  }, [data?.reps, sortKey, sortDir]);

  const kpis = data?.kpis || {};
  const paceCls = (kpis.collections || 0) >= (kpis.target_pace || 0) ? "good" : "bad";
  const progressPct = Math.min(
    100,
    Math.round((100 * (kpis.collections || 0)) / (kpis.target_month || 7000000))
  );

  return (
    <div className="sales-analytics-root">
      {/* Sticky Top Header */}
      <div className="sa-top">
        <div className="sa-top-in">
          <div className="sa-top-left">
            <h1>Sales Analytics</h1>
            {data?.generated_at && (
              <span className="sa-updated ms-2">updated {data.generated_at}</span>
            )}
          </div>

          <div className="sa-filters">
            {/* Range Segments */}
            <div className="sa-seg">
              <button
                type="button"
                className={range === "today" ? "on" : ""}
                onClick={() => setRange("today")}
              >
                Today
              </button>
              <button
                type="button"
                className={range === "7d" ? "on" : ""}
                onClick={() => setRange("7d")}
              >
                7 days
              </button>
              <button
                type="button"
                className={range === "mtd" ? "on" : ""}
                onClick={() => setRange("mtd")}
              >
                MTD
              </button>
              <button
                type="button"
                className={range === "90d" ? "on" : ""}
                onClick={() => setRange("90d")}
              >
                90 days
              </button>
            </div>

            {/* Team Dropdown */}
            <select
              className="sa-select"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
            >
              <option value="all">All teams</option>
              {teamsList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>

            {/* Source Dropdown */}
            <select
              className="sa-select"
              value={source}
              onChange={(e) => setSource(e.target.value)}
            >
              <option value="all">All sources</option>
              {sourcesList.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>

            {/* Refresh Button */}
            <button
              type="button"
              className="sa-refresh-btn"
              title="Refresh Data"
              onClick={fetchData}
            >
              <RotateCw size={14} className={loading ? "fa-spin" : ""} />
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="sa-wrap">
        {loading && !data && (
          <div className="sa-loading-overlay">
            <div className="sa-spinner"></div>
            <p className="text-muted small">Loading sales analytics from database...</p>
          </div>
        )}

        {error && !data && (
          <div className="alert alert-danger d-flex align-items-center justify-content-between p-3 rounded-3 my-4">
            <div className="d-flex align-items-center gap-2">
              <AlertCircle size={20} className="text-danger" />
              <span>{error}</span>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={fetchData}
            >
              Retry
            </button>
          </div>
        )}

        {data && (
          <>
            {/* KPI Cards Row */}
            <div className="sa-kpis">
              <div className="sa-kpi">
                <div className="l">Collections ({data?.period_label || "MTD"})</div>
                <div className="v">{fmtL(kpis.collections)}</div>
                <div className={`s ${paceCls}`}>
                  target pace {fmtL(kpis.target_pace)} · month {fmtL(kpis.target_month || 7000000)}
                </div>
                <div className="sa-prog">
                  <i style={{ width: `${progressPct}%` }}></i>
                </div>
              </div>

              <div className="sa-kpi">
                <div className="l">Paid orders</div>
                <div className="v">{kpis.orders || 0}</div>
                <div className={`s ${(kpis.aov || 0) >= 20000 ? "good" : "warn"}`}>
                  AOV {fmtINR(kpis.aov)}
                </div>
              </div>

              <div className="sa-kpi">
                <div className="l">Lead → paid conversion</div>
                <div className="v">{pct(kpis.conversion)}</div>
                <div className={`s ${(kpis.conversion || 0) >= 0.035 ? "good" : "bad"}`}>
                  benchmark 3.5%+
                </div>
              </div>

              <div className="sa-kpi">
                <div className="l">Dials · connect rate</div>
                <div className="v">{(kpis.dials || 0).toLocaleString("en-IN")}</div>
                <div className={`s ${(kpis.connect_rate || 0) >= 0.35 ? "good" : "warn"}`}>
                  {pct(kpis.connect_rate)} connected
                </div>
              </div>

              <div className="sa-kpi">
                <div className="l">Follow-ups overdue</div>
                <div className="v">{kpis.followups_overdue || 0}</div>
                <div className={`s ${(kpis.followups_overdue || 0) > 50 ? "bad" : "warn"}`}>
                  {kpis.followups_today || 0} due today
                </div>
              </div>

              <div className="sa-kpi">
                <div className="l">5-min SLA breaches</div>
                <div className="v">{kpis.sla_breaches || 0}</div>
                <div className={`s ${(kpis.sla_breaches || 0) > 0 ? "bad" : "good"}`}>
                  web/Google leads waiting &gt;5 min
                </div>
              </div>
            </div>

            {/* Dashboard Grid */}
            <div className="sa-grid">
              {/* Daily Collections vs Target */}
              <div className="sa-card sa-c8">
                <h2>Collections vs target — daily</h2>
                <p className="note">
                  Bars = collections booked per day. Line = cumulative vs the month's target pace. Excludes ₹1 mandate tokens.
                </p>
                <div className="sa-chart-box tall">
                  <canvas ref={chTrendRef}></canvas>
                </div>
              </div>

              {/* Lead Funnel */}
              <div className="sa-card sa-c4">
                <h2>Lead funnel — selected period</h2>
                <p className="note">New leads → contacted → prospect → paid. Hover a stage for drop-off.</p>
                <div className="sa-chart-box tall">
                  <canvas ref={chFunnelRef}></canvas>
                </div>
              </div>

              {/* Executive Leaderboard Table */}
              <div className="sa-card">
                <h2>Executive leaderboard</h2>
                <p className="note">
                  Click a column to sort. Conversion = paid ÷ fresh leads assigned in period. SLA = new-lead first-call within 5 minutes.
                </p>
                <div className="sa-scroll">
                  <table className="sa-table">
                    <thead>
                      <tr>
                        {[
                          ["name", "Executive", false],
                          ["collections", "Collections", true],
                          ["orders", "Orders", true],
                          ["aov", "AOV", true],
                          ["conversion", "Conv %", true],
                          ["dials", "Dials", true],
                          ["connect_rate", "Connect %", true],
                          ["followups_overdue", "Overdue FU", true],
                          ["sla_breaches", "SLA miss", true],
                          ["renewals_due", "Renewals due", true],
                        ].map(([k, label, isNum]) => (
                          <th
                            key={k}
                            className={isNum ? "num" : ""}
                            onClick={() => handleSort(k)}
                          >
                            {label}
                            {sortKey === k && (
                              <span className="arr">{sortDir < 0 ? " ▼" : " ▲"}</span>
                            )}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedReps.map((r, i) => (
                        <tr key={r.id || i}>
                          <td>
                            <b>{r.name}</b>
                          </td>
                          <td className="num">{fmtL(r.collections)}</td>
                          <td className="num">{r.orders}</td>
                          <td className="num">{fmtINR(r.aov)}</td>
                          <td className="num">
                            <span
                              className={`sa-pill ${
                                r.conversion >= 0.035
                                  ? "sa-p-good"
                                  : r.conversion >= 0.025
                                  ? "sa-p-warn"
                                  : "sa-p-crit"
                              }`}
                            >
                              {pct(r.conversion)}
                            </span>
                          </td>
                          <td className="num">{(r.dials || 0).toLocaleString("en-IN")}</td>
                          <td className="num">{pct(r.connect_rate)}</td>
                          <td className="num">
                            <span
                              className={`sa-pill ${
                                r.followups_overdue > 20
                                  ? "sa-p-crit"
                                  : r.followups_overdue > 8
                                  ? "sa-p-warn"
                                  : "sa-p-good"
                              }`}
                            >
                              {r.followups_overdue}
                            </span>
                          </td>
                          <td className="num">{r.sla_breaches}</td>
                          <td className="num">
                            <span className="sa-pill sa-p-info">{r.renewals_due}</span>
                          </td>
                        </tr>
                      ))}
                      {sortedReps.length === 0 && (
                        <tr>
                          <td colSpan="10" className="text-center py-4 text-muted">
                            No executive data available for the selected filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Source Performance */}
              <div className="sa-card sa-c6">
                <h2>Source performance</h2>
                <p className="note">Revenue per lead decides routing and ad spend — not lead count.</p>
                <div className="sa-chart-box">
                  <canvas ref={chSrcRef}></canvas>
                </div>
              </div>

              {/* Ticket Size Distribution */}
              <div className="sa-card sa-c6">
                <h2>Ticket-size distribution</h2>
                <p className="note">Anchor-pricing health: the goal is mass moving right of ₹20k.</p>
                <div className="sa-chart-box">
                  <canvas ref={chTicketRef}></canvas>
                </div>
              </div>

              {/* Follow-up Discipline */}
              <div className="sa-card sa-c4">
                <h2>Follow-up discipline</h2>
                <p className="note">Overdue = follow-up date passed with no logged activity.</p>
                <div className="sa-chart-box">
                  <canvas ref={chFollowRef}></canvas>
                </div>
              </div>

              {/* Renewal Radar */}
              <div className="sa-card sa-c4">
                <h2>Renewal radar</h2>
                <p className="note">Call at 70% credit burn — never after expiry.</p>
                <div className="sa-duelist">
                  {(data?.renewals_due || []).map((r, idx) => (
                    <div className="sa-due" key={idx}>
                      <div>
                        <b>{r.client}</b>
                        <div className="m">
                          {r.rep} · {r.plan}
                        </div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <span
                          className={`sa-pill ${
                            r.burn >= 0.9
                              ? "sa-p-crit"
                              : r.burn >= 0.7
                              ? "sa-p-warn"
                              : "sa-p-info"
                          }`}
                        >
                          {Math.round(100 * r.burn)}% burnt
                        </span>
                        <div className="m">{r.expires_in} days left</div>
                      </div>
                    </div>
                  ))}
                  {(!data?.renewals_due || data.renewals_due.length === 0) && (
                    <div className="m py-3 text-center" style={{ color: "var(--sa-muted)" }}>
                      No renewals due in window.
                    </div>
                  )}
                </div>
              </div>

              {/* New-lead Speed SLA */}
              <div className="sa-card sa-c4">
                <h2>New-lead speed (SLA)</h2>
                <p className="note">First call within 5 minutes of lead creation, by rep — website &amp; Google leads.</p>
                <div className="sa-chart-box">
                  <canvas ref={chSlaRef}></canvas>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Footer */}
      <footer className="sa-footer">
        Definitions: Collections = sum of Paid Amt (orders, status Paid, amount &gt; ₹2) · AOV = collections ÷ orders · Conversion = paid clients ÷ leads assigned in the period ·
        Connect rate = connected calls ÷ dials · Data refresh: this page re-queries on filter change.
        Internal tool — SEBI RA INH000017082 compliance rules apply to any client-facing reuse of these numbers.
      </footer>
    </div>
  );
};

export default SalesAnalysisDashboard;
